/* eslint-disable no-param-reassign */

/**
 * @file Surface state
 */
import { trackEvent } from '@@/bits/analytics'
import { setCookie } from '@@/bits/cookie'
import { buildUrlFromPath, clearSearchParam, transformUrl } from '@@/bits/location'
import { useGoogleDrivePickerStore } from '@@/pinia/google_drive_picker'
import { MobilePage } from '@@/types/dash'
import { useGoogleDrive } from '@@/vuecomposables/google_drive'
import type { ContentModeration, JsonAPIResource } from '@padlet/arvo'
import { deleteWall, GroupByTypes, saveWall, SortByTypes } from '@padlet/arvo'
import { shadeColor } from 'bits/color'
import { DELETE_WALL_CONFIRMATION_PARAMS } from 'bits/confirmation_dialog'
import { captureFetchException, captureNonNetworkFetchError } from 'bits/error_tracker'
import { isAppUsing } from 'bits/flip'
import { isValidFolderName, MAX_FOLDER_NAME_LENGTH } from 'bits/folder'
import window from 'bits/global'
import { __ } from 'bits/intl'
import { loginWithReferrerUrl, navigateTo, reload } from 'bits/location'
import { getNotificationPermission, registerForPushNotifications } from 'bits/notifications'
import { numberToHumanSize } from 'bits/numbers_helper'
import safeStorage from 'bits/safe_storage'
import { vSet } from 'bits/vue'
import { Wall as DashboardWallApi } from 'dashboard/padlet_api'
import { LibraryType, ParamCommentModerationActionResult, ParamPostModerationActionResult } from 'enums'
import anxiousFace from 'images/anxious_face.svg'
import { sortBy } from 'lodash-es'
import { useSurfaceContainerSizeStore } from 'pinia/surface_container_size'
import { useSurfaceFilterStore } from 'pinia/surface_filter'
import { useReactionsStore } from 'pinia/surface_reactions'
import { useSurfaceSectionsStore } from 'pinia/surface_sections'
import { useSurfaceSharePanelStore } from 'pinia/surface_share_panel'
import PadletApi from 'surface/padlet_api'
import type {
  CanSetWallPrivacyOptions,
  Id,
  Library,
  UserLibrarySettingsApiResponse,
  WallFollowInformation,
} from 'types'
import type { Plugin, StoreOptions } from 'vuex'
import activityPanel from 'vuexstore/modules/activity_panel'
import attachment from 'vuexstore/modules/attachment'
import browser from 'vuexstore/modules/browser'
import wallCollections from 'vuexstore/modules/collections'
import comment from 'vuexstore/modules/comment'
import composerModal from 'vuexstore/modules/composer_modal'
import composerModalAlert from 'vuexstore/modules/composer_modal_alert'
import composerModalTagInputs from 'vuexstore/modules/composer_modal_tag_inputs'
import contentPicker from 'vuexstore/modules/content_picker'
import contributingStatus from 'vuexstore/modules/contributing_status'
import draft from 'vuexstore/modules/draft'
import dragAndDropEvents from 'vuexstore/modules/drag_and_drop_events'
import electronApp from 'vuexstore/modules/electron_app'
import alertDialog from 'vuexstore/modules/global_alert_dialog'
import type { ConfirmationDialogPayload } from 'vuexstore/modules/global_confirmation_dialog'
import confirmationDialog, { OzConfirmationDialogBoxButtonScheme } from 'vuexstore/modules/global_confirmation_dialog'
import inputDialog from 'vuexstore/modules/global_input_dialog'
import globalSnackbar, { SnackbarNotificationType } from 'vuexstore/modules/global_snackbar'
import imagePicker from 'vuexstore/modules/image_picker'
import libraryPlans, { UpgradeStep as LibraryUpgradeStep } from 'vuexstore/modules/library_plans'
import map from 'vuexstore/modules/map'
import nativeApp from 'vuexstore/modules/native_app'
import plans, { UpgradeSource, UpgradeStep } from 'vuexstore/modules/plans'
import post from 'vuexstore/modules/post'
import postAction from 'vuexstore/modules/post_action'
import postConnection from 'vuexstore/modules/post_connection'
import reaction from 'vuexstore/modules/reaction'
import realtime from 'vuexstore/modules/realtime'
import section from 'vuexstore/modules/section'
import share from 'vuexstore/modules/share'
import snackbars from 'vuexstore/modules/snackbars'
import settings from 'vuexstore/modules/surface_settings'
import table from 'vuexstore/modules/table'
import userContributor from 'vuexstore/modules/user_contributor'
import wallAccessSettings from 'vuexstore/modules/wall_access_settings'
import addressListener from 'vuexstore/plugins/address_listener'
import electronBridgeListener from 'vuexstore/plugins/electron_bridge_listener'
import logger, { shouldLog } from 'vuexstore/plugins/logger'
import type { RootState, SurfaceState } from 'vuexstore/surface/types'

// How to decide what to put in state vs getters
// State should have either:
// 1. things we sync with the server
// 2. things that change
// 3. things that are a function of the device
// Everything else is in getters.

// Keep all lists alphabetical
const plugins: Array<Plugin<RootState>> = [
  addressListener,
  electronBridgeListener<RootState>('dashboard'),
  // pepinTracker    // Uncomment to track all state changes
]
if (shouldLog) plugins.push(logger)

export const blankState = (): SurfaceState => ({
  /* CONSTANTS */
  arvoConfig: undefined,
  canPostMessage: false,
  constants: {},
  cookiesEnabled: false,
  csrfToken: '',
  deviceId: '',
  dir: '',
  dpr: 1,
  environment: 'production',
  isAndroid: false,
  isApp: false,
  isChrome: false,
  isDesktop: false,
  isElectronApp: false,
  isEdge: false,
  isEmbedded: false,
  isFirefox: false,
  isIE: false,
  isIOS: false,
  isIPhone: false,
  isIPhoneX: false,
  isMac: false,
  isMobile: false,
  isMousable: false,
  isNativeMenu: false,
  isOldApp: false,
  isPhone: false,
  isSafari: false,
  isTablet: false,
  isTouchable: false,
  isWindows: false,
  locale: 'en',
  modalPostEdit: false,
  nativePostEdit: false,
  release: '',
  uid: '',
  xHeader: false,
  homeUrl: '',

  /* OBJECTS */
  tenant: {},
  user: {},
  wall: {},

  /* OTHERS */
  commentsPanelPostId: null,
  convertLayoutErrorMessage: '',
  editorHeight: 0,
  fileThatExceededSize: null,
  headerHeight: 136,
  isOnline: true,
  wallLastSyncTime: null,
  wishListMounted: false,
  xContributorsList: false,
  xExtensionRequired: false,
  xWallQuotaExceeded: false,
  xEditBookmarksDialog: false,
  editBookmarksDialogPopoverAnchor: null,
  isAPostDragging: false,
  isSlideshow: false,
  isGeneratingPDF: false,
  breakoutSectionId: null,
  sectionBreakoutIdentifier: null,
  isSubmissionRequest: false,
  isFirstView: false,
  reportedPostId: null,
  isUsedInEducationalContext: false,
  xFilterProfanitySetting: false,

  /* PANELS */
  xCommentsPanel: false,
  xConvertLayoutPanel: false,
  xInfo: false,
  xPermitNotification: false,
  xReloadNotification: false,
  xRemakePanel: false,
  xToolMenu: false,
  xReportPanel: false,
  xDevelopersPanel: false,
  xActionBarMoreMenu: false,
  isSkoletubeUser: false,

  /* PERMISSIONS */
  amIOwner: false,
  amILibraryOwner: false,
  canIAdminister: false,
  canIEdit: false,
  canIMakeWalls: false,
  canIModerate: false,
  canIRemake: false,
  canIWrite: false,
  isSubscriptionPaused: false,
  isReadOnly: true,
  isScreenshotMode: false,
  amICollaborator: false,
  blockedContentProviders: new Set<string>(),
  canIMakePrivateWalls: false,
  canIMakeOrgWideDashboardWalls: false,
  canIMakeOrgWideHiddenWalls: false,
  canIMakePasswordWalls: false,
  canIMakeSecretWalls: false,
  canIMakeLibraryWideDashboardWalls: false,
  canIMakeLibraryWideHiddenWalls: false,
  canIMakeSecretLoggedInVisitorWalls: false,
  canIMakePublicWalls: false,

  /* LIBRARIES */
  library: {},
  creatableLibraries: [], // Libraries where user can create
  viewableLibraries: [], // Libraries where user can view
  userLibrarySettings: {},

  /* EMBED */
  loginAndRedirectToWallUrl: '',
  loginAndRedirectToBuilderProfileUrl: '',
  loginAndRedirectUrlExpireDuration: 0,

  /* FOLLOWS */
  wall_follows: {
    current_user_follows_wall: false,
    wall_follow_id: null,
    wall_follows_count: 0,
  },

  shouldFallbackWsDataFetchToRest: false,
})

export const storeStructure: StoreOptions<RootState> = {
  plugins,
  modules: {
    activityPanel,
    attachment,
    nativeApp,
    imagePicker,
    reaction,
    postConnection,
    comment,
    section,
    post,
    postAction,
    table,
    share,
    settings,
    wallAccessSettings,
    contentPicker,
    confirmationDialog,
    alertDialog,
    inputDialog,
    map,
    snackbars,
    globalSnackbar,
    realtime,
    browser,
    userContributor,
    composerModal,
    draft,
    composerModalAlert,
    composerModalTagInputs,
    electronApp,
    dragAndDropEvents,
    contributingStatus,
    plans,
    libraryPlans,
    wallCollections,
  },
  state: blankState() as RootState,
  getters: {
    // constants
    sectionableLayouts: (state) => state.constants.sectionableLayouts,
    // wall
    wall: (state) => state.wall,
    displayAttrs: (state, getters) =>
      getters['settings/isPreviewing'] ? getters['settings/previewAttributes'] : state.wall,
    wallId: (state) => state.wall.id,
    wallUpdatedAt: (state) => state.wall.updated_at,
    wallCreatedAt: (state) => state.wall.created_at,
    originalBackground: (state) => state.wall.background,
    title: (state, getters) => getters.displayAttrs.title,
    description: (state, getters) => getters.displayAttrs.description,
    background: (state, getters) => getters.displayAttrs.background,
    portrait: (state, getters) => getters.displayAttrs.portrait,
    viz: (state, getters) => getters.displayAttrs.viz, // reflects true WallViz. (for styling, use format)
    contentModerationSetting: (state, getters): ContentModeration => getters.displayAttrs.content_moderation,
    postSize: (state, getters) => getters.displayAttrs.post_size,
    user: (state) => state.user,
    newPostLocation: (state, getters) => getters.displayAttrs.new_wish_loc,
    canConfigNewPostLocation: (state) => state.constants.newPostLocationConfigurableLayouts.includes(state.wall.viz),
    filterProfanity: (state, getters) => getters.displayAttrs.filter_profanity,
    requireApproval: (state, getters) => getters.displayAttrs.require_approval,
    isUsedInEducationalContext: (state) => state.isUsedInEducationalContext,
    xFilterProfanitySetting: (state) => state.xFilterProfanitySetting ?? false,
    // Background/skin, the skin colors are based on the background for now but they may change in the future
    backgroundColor: (state, getters) =>
      getters.displayAttrs.background.primary_color_as_rgb ?? getters.displayAttrs.background.dominant_color_as_rgb,
    headerColor: (state, getters) =>
      getters.isMap
        ? !getters.backgroundColor
          ? getters.colorScheme === 'light'
            ? '#FFFFFF'
            : '#000000'
          : getters.backgroundColor
        : getters.isTitleBarTransparent
        ? 'transparent'
        : getters.backgroundColor,
    backgroundLuminance: (state, getters) => getters.displayAttrs.background.luminance_name,
    isBackgroundBlurred: (state, getters) =>
      Array.isArray(getters.background.effects)
        ? getters.background.effects.includes('blur')
        : getters.background.effect === 'blur',
    skinPrimaryColorAsRGB: (state, getters) => getters.displayAttrs.background.primary_color_as_rgb,
    skinPrimaryVariantColorAsRGB: (_, getters) => {
      if (getters.skinPrimaryColorAsRGB != null) {
        return getters.skinPrimaryColorAsRGB
      }
      const originalColor = getters.displayAttrs.background.dominant_color_as_rgb
      if (getters.displayAttrs.background.type === 'solid_color' && originalColor != null) {
        // 25% ligher for light backgrounds, 10% darker for dark backgrounds
        return shadeColor(originalColor, getters.backgroundLuminance === 'light' ? 25 : -10)
      }
      return originalColor
    },
    skinAccentColorAsRGB: (state, getters) => getters.displayAttrs.background.accent_color_as_rgb,
    skinPrimaryTextColor: (state, getters) => getters.displayAttrs.background.primary_text_color,
    skinAccentTextColor: (state, getters) => getters.displayAttrs.background.accent_text_color,

    colorScheme: (state, getters) => getters.displayAttrs.color_scheme,
    themeId: (state, getters) => getters.displayAttrs.theme_id, // used for map
    fontId: (state, getters) => getters.displayAttrs.font_id,
    isCommentable: (state, getters) => getters.displayAttrs.is_commentable,
    isReactable: (state, getters) => getters.displayAttrs.is_reactable,
    isRemakeable: (_, getters) => getters.displayAttrs.is_remakeable,
    reactionData: (state, getters) => getters.displayAttrs.reaction_data,
    name: (state, getters) => getters.displayAttrs.name,
    xAuthor: (state, getters) => getters.displayAttrs.show_author,
    isTitleBarTransparent: (state, getters) => getters.displayAttrs.is_title_bar_transparent,
    isSlideshow: (state, getters) => state.isSlideshow,
    breakoutSectionId: (state, getters) => state.breakoutSectionId,
    isGeneratingPDF: (state, getters) => state.isGeneratingPDF,
    isSectionBreakout: (state, getters) => state.breakoutSectionId !== null,
    sectionBreakoutIdentifier: (state, getters) => state.sectionBreakoutIdentifier,
    isSubmissionRequest: (state, getters) => state.isSubmissionRequest,

    builder: (state) => state.wall.builder,
    namespace: (state) => state.wall.namespace,
    convertLayoutErrorMessage: (state) => state.convertLayoutErrorMessage,
    isOrgWall: (state) => state.wall.tenant_id > 1,
    coverPostId: (state) => state.wall.cover_wish_id,
    publicKey: (state) => state.wall.public_key,
    address: (state) => state.wall.address,
    domainName: (state) => state.tenant.domain_name,
    tenantName: (state) => state.tenant.name,
    isTemplate: (state) => state.wall.is_template,

    // Permissions
    canIAdminister: (state) => state.canIAdminister,
    canIModerate: (state) => state.canIModerate,
    canIWrite: (state) => state.canIWrite,
    canIEdit: (state) => state.canIEdit,
    canIMakeWalls: (state) => state.canIMakeWalls,
    canIRemake: (state) => state.canIRemake,
    canIBookmarkWall: (state, getters) => getters.amIRegistered,
    canIChangeCoverPost: (state) => state.canIAdminister,
    amIOwner: (state) => state.amIOwner,
    amILibraryOwner: (state) => state.amILibraryOwner,
    amICollaborator: (state) => state.amICollaborator,
    amIRegistered: (state) => !!state.user.registered_at,
    isSubscriptionPaused: (state) => state.isSubscriptionPaused,
    canICreateWall: (state) => state.user.quota.can_make,
    canIPostWithoutWaitingForApproval: (state, getters) =>
      getters.canIWrite && (getters.canIModerate || !getters.requireApproval),
    blockedContentProviders: (state) => state.blockedContentProviders,
    canIMakePrivateWalls: (state) => state.canIMakePrivateWalls,
    canIMakeOrgWideDashboardWalls: (state) => state.canIMakeOrgWideDashboardWalls,
    canIMakeOrgWideHiddenWalls: (state) => state.canIMakeOrgWideHiddenWalls,
    canIMakePasswordWalls: (state) => state.canIMakePasswordWalls,
    canIMakeSecretWalls: (state) => state.canIMakeSecretWalls,
    canIMakeLibraryWideDashboardWalls: (state) => state.canIMakeLibraryWideDashboardWalls,
    canIMakeLibraryWideHiddenWalls: (state) => state.canIMakeLibraryWideHiddenWalls,
    canIMakeSecretLoggedInVisitorWalls: (state) => state.canIMakeSecretLoggedInVisitorWalls,
    canIMakePublicWalls: (state) => state.canIMakePublicWalls,
    shouldPostsBeAutoModerated: (state, getters) => !getters.canIEdit && getters.contentModerationSetting === 'auto',

    isApp: (state) => state.isApp,
    isPhone: (state) => state.isPhone,
    isDesktop: (state) => state.isDesktop,
    isMousable: (state) => state.isMousable,
    dir: (state) => state.dir,
    isReadOnly: (state) => state.isReadOnly,
    isScreenshotMode: (state) => state.isScreenshotMode,
    isPostable: (state) => (state.canIWrite && !state.isReadOnly) || state.isSubmissionRequest,
    format: (state, getters) => {
      // reflects layout format for display styling (for true wall format, use viz)
      if (getters.isSectionBreakout === true) return getters.displayAttrs.viz === 'map' ? 'map' : 'grid'
      return getters.displayAttrs.viz
    },
    isCanvas: (state, getters) => getters.format === 'free',
    isMap: (state, getters) => getters.format === 'map',
    isShelf: (state, getters) => getters.format === 'shelf',
    isStream: (state, getters) => getters.format === 'stream',
    isGrid: (state, getters) => getters.format === 'grid',
    isTable: (state, getters) => getters.format === 'table',
    isTimeline: (state, getters) => getters.format === 'timeline' || getters.format === 'timeline_v2',
    isTimelineV1: (state, getters) => getters.format === 'timeline',
    isTimelineV2: (state, getters) => getters.format === 'timeline_v2',
    isMatrix: (state, getters) => getters.format === 'matrix',
    isSectioned: (_state, getters) => (getters.isShelf as boolean) || getters.wishGroupBy === GroupByTypes.Section,
    isFirstView: (state) => state.isFirstView,
    isPrivate: (_, getters) => getters['wallAccessSettings/isPrivacyTypePrivate'],
    isPasswordProtected: (_, getters) => getters['wallAccessSettings/isPrivacyTypePasswordProtected'],
    isOrgWide: (_, getters) => getters['wallAccessSettings/isPrivacyTypeOrgWide'],
    isLoggedInUsersOnly: (_, getters) => getters['wallAccessSettings/isPrivacyTypeLoggedInUsersOnly'],
    isLibraryWide: (_, getters) => getters['wallAccessSettings/isPrivacyTypeLibraryWide'],
    canHaveConnections: (state, getters) => getters.isCanvas || getters.isMap,
    xUnpublishedPostPosition: (state, getters) => getters.isTable,
    uploadLimit: (state) => state.wall.upload_limit,
    advertizedUploadLimit: (state) => state.wall.advertized_upload_limit,
    isPremiumWall: (state) => state.wall.is_premium, // wall made by a pro user
    links: (state) => state.wall.links,
    xSettingsPanel: (state, getters) => getters['settings/isOpen'],
    isOverlayVisible: (state, getters) => {
      return (
        // Side panels
        state.xConvertLayoutPanel ||
        state.xInfo || // About this padlet panel
        state.xRemakePanel ||
        getters.xCopyPostPanel ||
        getters.xReportPanel ||
        getters.xDevelopersPanel ||
        getters.xSettingsPanel ||
        useSurfaceSharePanelStore().xSurfaceSharePanel ||
        getters.xTransferPostPanel ||
        getters['activityPanel/xActivityPanel'] ||
        getters['map/isPickingLocation'] ||
        // Modals
        state.comment.activeNewCommentPostId || // Comment modal on mobile
        state.comment.commentBeingEditedId || // Comment modal on mobile
        state.reaction.postIdBeingReactedTo || // Reaction modal on mobile
        state.xContributorsList ||
        state.xWallQuotaExceeded ||
        getters.xEditBookmarksDialog ||
        getters['contentPicker/xContentPickerPanel'] ||
        getters['imagePicker/xImagePickerSources'] ||
        getters['libraryPlans/xUpgradeModal'] ||
        getters['plans/xUpgradeModal'] ||
        getters['postAction/xPostAction'] ||
        useSurfaceFilterStore().xFilterModal ||
        useSurfaceSectionsStore().xSectionRenameModal ||
        // Menus
        state.comment.actionMenuCommentId ||
        state.section.actionMenuSectionId ||
        getters.xActionBarMoreMenu ||
        useSurfaceSectionsStore().sectionUnderActionId ||
        // Dialogs
        getters['alertDialog/isAlertOpen'] ||
        getters['confirmationDialog/isConfirmationOpen'] ||
        // Legacy
        state.xCommentsPanel ||
        state.xToolMenu ||
        getters['postAction/zoomedPostCid'] ||
        getters['section/sectionUnderActionId']
      )
    },
    xHeader: (state) => state.xHeader,
    headerHeight: (state) => state.headerHeight,
    isWallSyncNeeded: (state, getters) => {
      const lastSycnedTime = state.wallLastSyncTime
      if (!lastSycnedTime) return true
      return getters['realtime/isRealtimeDownSinceLastSync'](lastSycnedTime)
    },
    xEditBookmarksDialog: (state) => state.xEditBookmarksDialog,
    editBookmarksDialogPopoverAnchor: (state) => state.editBookmarksDialogPopoverAnchor,

    /* POST MODIFIERS */
    xTallAttachmentPickerBox: (state, getters) => getters.isMap || getters.isTimeline || getters.isTable,
    // pagination - only posts for now
    isInitialPaginatedDataLoadDone: (state, getters) => getters['post/isInitialPaginatedDataLoadDone'],
    nativePostEdit: (state) => state.nativePostEdit,
    modalPostEdit: (state) => state.modalPostEdit,
    xNewComposerModalPanel: (_, getters) => {
      // show composer modal panel even if canIWrite = false
      // Allow users to see their unpublished drafts, even if their write permissions have been revoked.
      return !getters.nativePostEdit && !getters.isReadOnly
    },

    /* Surface Layout */
    xActionBarMoreMenu: (state): boolean => state.xActionBarMoreMenu,
    isDesktopLayout: (_, getters) =>
      !getters.isApp && !useSurfaceContainerSizeStore().isContainerSmallerThanTabletLandscape,
    isMobileLayout: (_, getters) =>
      !getters.isApp && useSurfaceContainerSizeStore().isContainerSmallerThanTabletLandscape,
    canFilterPosts: (_, getters) => !getters.isApp,
    canUseSections: (_, getters) => getters.sectionableLayouts.includes(getters.format),
    defaultWishSortBy: (state, getters) => {
      // matches default sort_by in wall_wish_arrangements.rb
      if (getters.isTimeline || getters.isShelf) return SortByTypes.ManualNewPostsLast
      return SortByTypes.ManualNewPostsFirst
    },
    wishGroupBy: (state, getters) => {
      if (!getters.canUseSections) return GroupByTypes.None
      return state.wall.wish_arrangement.group_by ?? GroupByTypes.None
    },
    wishSortBy: (state, getters) => {
      return state.wall.wish_arrangement?.sort_by
    },
    isSectionable: (state) => state.wall.wish_arrangement.is_sectionable,

    isAPostDragging: (state) => state.isAPostDragging,
    /* User Oauth Token */
    oauthToken: (state) => state.arvoConfig != null && state.arvoConfig.token && state.arvoConfig.token.oauthToken,

    // Libraries
    isLibraryWall: (state) => Boolean(state.wall.library_id),
    wallLibraryId: (state) => state.wall.library_id,
    creatableLibraries: (state) => sortBy(state.creatableLibraries, 'joinedAt'),
    currentVisibleAndCreatableLibraries: (_, getters) => {
      // A library is considered visible if it is the current wall's library or has visibility as true
      return getters.creatableLibraries.filter(
        (creatableLibrary: Library) =>
          creatableLibrary.visible === true || creatableLibrary.id === getters.wallLibraryId,
      )
    },
    creatableLibraryById: (_, getters) => (libraryId) =>
      getters.creatableLibraries.find((creatableLibrary) => creatableLibrary?.id == libraryId),
    viewableLibraries: (state) => sortBy(state.viewableLibraries, 'joinedAt'),
    wallInViewableLibraries: (state, getters) =>
      getters.viewableLibraries.map((library) => parseInt(library.id)).includes(state.wall.library_id),
    currentVisibleAndViewableLibraries: (_, getters) => {
      // A library is considered visible if it is the current wall's library or has visibility as true
      return getters.viewableLibraries.filter(
        (viewableLibrary: Library) => viewableLibrary.visible === true || viewableLibrary.id === getters.wallLibraryId,
      )
    },
    wallInCreatableLibraries: (state, getters) =>
      getters.creatableLibraries.map((library) => parseInt(library.id)).includes(state.wall.library_id),
    library: (state) => state.library,
    libraryName: (state) => state.library?.name,
    librarySlug: (state) => state.library?.slug,
    libraryDashboardUrl: (state) => state.library?.dashboard_url,
    libraryMakersCount: (state) => state.library?.makers_count,
    libraryType: (state) => state.library?.libraryType,
    isSchoolLibrary: (state) => state.library?.libraryType === LibraryType.School,
    isClassroomLibrary: (state) => state.library?.libraryType === LibraryType.Classroom,
    libraryWallsUsed: (state) => state.library?.walls_used,
    organizationName: (_, getters) => (getters.isLibraryWall ? getters.libraryName : getters.tenantName),
    defaultLibrary: (state) => state.userLibrarySettings?.defaultLibrary,
    librarySettings: (state) => state.userLibrarySettings?.librarySettings,
    isPersonalLibraryVisible: (_, getters) =>
      getters.librarySettings?.find((librarySetting) => librarySetting.libraryType === LibraryType.Personal)?.visible,
    xPersonalLibrary: (_, getters) => {
      // Show personal library if the user owns the wall and it is a non-library wall
      if (getters.isBuilderCurrentUser === true && getters.isLibraryWall === false) {
        return true
      }

      // We show the personal library for non-native wall
      if (getters.isOrgWall === true) return true

      return getters.isPersonalLibraryVisible === true
    },
    isLibraryDeprecatedFreeTier: (state) => state.library.isDeprecatedFreeTier,
    isBuilderCurrentUser: (_, getters) => getters.user.id === getters.builder.id,
    defaultLibraryId: (_, getters) => {
      if (getters.wallInCreatableLibraries === true) {
        return getters.wall.library_id
      }

      // Set personal library as the default if the user owns the wall
      if (getters.isBuilderCurrentUser === true) {
        return null
      }

      if (getters.defaultLibrary?.libraryId !== undefined) {
        const PERSONAL_LIBRARY_ID = ''

        // Return the user's personal library is selected as the default
        // null represents the user's personal library
        if (getters.defaultLibrary.libraryId === PERSONAL_LIBRARY_ID) {
          return null
        }

        return getters.defaultLibrary.libraryId
      }

      return null
    },
    // Embed
    loginAndRedirectToWallUrl: (state) => state.loginAndRedirectToWallUrl,
    loginAndRedirectToBuilderProfileUrl: (state) => state.loginAndRedirectToBuilderProfileUrl,
    loginAndRedirectUrlExpireDuration: (state) => state.loginAndRedirectUrlExpireDuration,
    homeUrl: (state) => state.homeUrl,
    // Follows
    wallFollowId: (state): Id | null => state.wall_follows.wall_follow_id,
    wallFollowsCount: (state): number => state.wall_follows.wall_follows_count,
    amIFollowingWall: (state): boolean => state.wall_follows.current_user_follows_wall,
    // Abuse Report
    xReportPanel: (state): boolean => state.xReportPanel,
    xDevelopersPanel: (state): boolean => state.xDevelopersPanel,
    reportedPostId: (state): Id | null => state.reportedPostId,
  },
  mutations: {
    INITIALIZE_STATE(state, data) {
      Object.assign(state, data)
    },
    CHANGE_CONNECTIVITY(state, data) {
      state.isOnline = data.isOnline
    },
    CHANGE_DRAGGING(state, { isAPostDragging }) {
      state.isAPostDragging = isAPostDragging
    },
    HIDE_CONTRIBUTORS_LIST(state) {
      state.xContributorsList = false
    },
    HIDE_EXTENSION_REQUIRED(state) {
      state.xExtensionRequired = false
    },
    UPDATE_FILE_THAT_EXCEEDED_SIZE(state, file) {
      state.fileThatExceededSize = file
    },
    HIDE_INFO(state) {
      state.xInfo = false
    },
    HIDE_WALL_QUOTA_EXCEEDED(state) {
      state.xWallQuotaExceeded = false
    },
    HIDE_COMMENTS_PANEL(state) {
      state.xCommentsPanel = false
      state.commentsPanelPostId = null
    },
    HIDE_PERMIT_NOTIFICATION(state) {
      state.xPermitNotification = false
    },
    SHOW_RELOAD_NOTIFICATION(state) {
      state.xReloadNotification = true
    },
    HIDE_RELOAD_NOTIFICATION(state) {
      state.xReloadNotification = false
    },
    SHOW_CONTRIBUTORS_LIST(state) {
      state.xContributorsList = true
    },
    SHOW_EXTENSION_REQUIRED(state) {
      state.xExtensionRequired = true
    },
    SHOW_INFO(state) {
      state.xInfo = true
    },
    SHOW_WALL_QUOTA_EXCEEDED(state) {
      state.xWallQuotaExceeded = true
    },
    SHOW_COMMENTS_PANEL(state, postId) {
      state.xCommentsPanel = true
      state.commentsPanelPostId = postId
    },
    SHOW_PERMIT_NOTIFICATION(state) {
      state.xPermitNotification = true
    },
    RESIZE_EDITOR(state, height) {
      state.editorHeight = height
    },
    RESIZE_HEADER(state, height) {
      state.headerHeight = height
    },
    SHOW_CONVERT_LAYOUT_PANEL(state) {
      state.xConvertLayoutPanel = true
    },
    HIDE_CONVERT_LAYOUT_PANEL(state) {
      state.xConvertLayoutPanel = false
    },
    CONVERT_LAYOUT_FAILED(state, message) {
      state.convertLayoutErrorMessage = message
    },
    SHOW_TOOL_MENU(state) {
      state.xToolMenu = true
    },
    HIDE_TOOL_MENU(state) {
      state.xToolMenu = false
    },
    SHOW_REMAKE_PANEL(state) {
      state.xRemakePanel = true
    },
    HIDE_REMAKE_PANEL(state) {
      state.xRemakePanel = false
    },
    SHOW_EDIT_BOOKMARKS_DIALOG(state, anchor = null) {
      if (anchor) state.editBookmarksDialogPopoverAnchor = anchor
      state.xEditBookmarksDialog = true
    },
    HIDE_EDIT_BOOKMARKS_DIALOG(state) {
      state.xEditBookmarksDialog = false
    },

    UPDATE_WALL(state, changes) {
      // Do this so that the whole wall object will not be reactively updated
      // but rather just each of the individual properties will be.
      Object.keys(changes).forEach((c) => {
        vSet(state.wall, c, changes[c])
      })
    },
    ANNOUNCE_WISH_LIST_MOUNTED(state) {
      state.wishListMounted = true
    },
    REFRESH_DONE(state) {
      state.wallLastSyncTime = Date.now()
    },
    SET_CREATABLE_LIBRARIES(state, { libraries }) {
      state.creatableLibraries = libraries
    },
    SET_USER_LIBRARY_SETTINGS(state, userLibrarySettings: UserLibrarySettingsApiResponse) {
      state.userLibrarySettings = userLibrarySettings
    },
    SET_VIEWABLE_LIBRARIES(state, { libraries }) {
      state.viewableLibraries = libraries
    },
    UPDATE_WALL_FOLLOWS(state, wallFollows: Partial<WallFollowInformation>): void {
      state.wall_follows = Object.assign(state.wall_follows, wallFollows)
    },
    SHOW_REPORT_PANEL(state, postId?: Id): void {
      state.xReportPanel = true
      state.reportedPostId = postId ?? null
    },
    HIDE_REPORT_PANEL(state): void {
      state.xReportPanel = false
      state.reportedPostId = null
    },
    SHOW_DEVELOPERS_PANEL(state): void {
      state.xDevelopersPanel = true
    },
    HIDE_DEVELOPERS_PANEL(state): void {
      state.xDevelopersPanel = false
    },
    SHOW_ACTION_BAR_MORE_MENU(state): void {
      state.xActionBarMoreMenu = true
    },
    HIDE_ACTION_BAR_MORE_MENU(state): void {
      state.xActionBarMoreMenu = false
    },
    MARK_WALL_AS_TEMPLATE(state): void {
      vSet(state.wall, 'is_template', true)
    },
    UNMARK_WALL_AS_TEMPLATE(state): void {
      vSet(state.wall, 'is_template', false)
    },
    SET_PRIVACY_OPTION_PERMISSIONS(state, privacyOptionsPermissions): void {
      const privacyOptionToStateNameHash = {
        private: 'canIMakePrivateWalls',
        password_protected: 'canIMakePasswordWalls',
        logged_in_users_only: 'canIMakeSecretLoggedInVisitorWalls',
        secret: 'canIMakeSecretWalls',
        public: 'canIMakePublicWalls',
        org_wide_unlisted: 'canIMakeOrgWideHiddenWalls',
        org_wide_listed: 'canIMakeOrgWideDashboardWalls',
        library_wide_unlisted: 'canIMakeLibraryWideHiddenWalls',
        library_wide_listed: 'canIMakeLibraryWideDashboardWalls',
      }

      privacyOptionsPermissions.forEach((privacyOptionPermission) => {
        state[privacyOptionToStateNameHash[privacyOptionPermission.name]] = privacyOptionPermission.canSet
      })
    },
    SET_SHOULD_USE_REST_API(state, shouldFallbackWsDataFetchToRest: boolean): void {
      state.shouldFallbackWsDataFetchToRest = shouldFallbackWsDataFetchToRest
    },
  },
  actions: {
    initializeState({ commit, dispatch, getters }, data) {
      const {
        postAction: { expandedPostCid },
        brahmsToken,
        deviceId,
        firstViewAfterInviteAccepted,
        paramPostModerationActionResult,
        paramCommentModerationActionResult,
        ...rootStoreState
      } = data

      const userId = rootStoreState.user.id
      const wallId = rootStoreState.wall.id

      commit('INITIALIZE_STATE', rootStoreState)
      // Log that the user has created a padlet. We are doing this to see user journeys.
      if (getters.isFirstView) {
        trackEvent('Surface', 'Created wall', rootStoreState.wall.title, null, { wallId })
      }

      dispatch('browser/initializeState')
      dispatch('realtime/initializeState', {
        brahmsToken,
        deviceId,
        pingContributingStatusToken: rootStoreState.constants.pingContributingStatusToken,
        userId,
      })
      if (expandedPostCid) {
        if (!getters['post/postEntitiesByCid'][expandedPostCid]) {
          // When a post is opened from URL, rootGetters['postAction/expandedPost'] would be null
          // since fetchWishes would have not finished
          dispatch('post/fetchSinglePost', { postId: parseInt(expandedPostCid.replace(/\D/g, ' '), 10) }).finally(
            () => {
              // Sometimes at this point all wishes are fetched. As long as the expanded post is fetched,
              // we can expand and show it first
              dispatch('postAction/expandPost', { postCid: expandedPostCid, xTextPanel: true })
            },
          )
        } else {
          dispatch('postAction/expandPost', { postCid: expandedPostCid, xTextPanel: true })
        }
      }
      dispatch('electronApp/setCurrentUser', rootStoreState.user)
      if (getters.amIRegistered) {
        if (getters.amIOwner) {
          dispatch('plans/fetchPlans')
          dispatch('plans/fetchAdvertisedQuotas')
        }

        dispatch('wallCollections/setActiveUser', userId)
        dispatch('wallCollections/switchAccountKey', { type: 'user', id: userId })
        dispatch('wallCollections/setActiveWall', wallId)
        dispatch('wallCollections/fetchBookmarks', { wallId })
        dispatch('wallCollections/fetchFolders', { userId })
      }

      if (getters.isSectionable) {
        const defaultSectionId = rootStoreState.wall?.wish_arrangement?.default_section_id
        if (defaultSectionId != null) dispatch('section/changeDefaultSection', defaultSectionId)
      }

      // Show invite accepted toast if first view after invite accepted.
      if (firstViewAfterInviteAccepted) {
        dispatch('snackbar', {
          notificationType: SnackbarNotificationType.success,
          message: __('Invite accepted'),
          timeout: 3000,
        })
      }

      void dispatch('handlePostModerationParams', paramPostModerationActionResult)
      void dispatch('handleCommentModerationParams', paramCommentModerationActionResult)
    },
    refreshPadletStartingState({ commit }, payload) {
      commit('INITIALIZE_STATE', payload)
    },
    reloadPage() {
      reload()
    },
    changeConnectivity({ commit }, data) {
      commit('CHANGE_CONNECTIVITY', data)
    },
    changeDragging({ commit }, { isAPostDragging }) {
      commit('CHANGE_DRAGGING', { isAPostDragging })
    },
    handleCommentModerationParams(
      { dispatch },
      paramCommentModerationActionResult: ParamCommentModerationActionResult,
    ) {
      switch (paramCommentModerationActionResult) {
        case ParamCommentModerationActionResult.APPROVE_SUCCESS:
          void dispatch('snackbar', {
            notificationType: SnackbarNotificationType.success,
            message: __('Comment approved'),
            timeout: 3000,
          })
          break
        case ParamCommentModerationActionResult.REJECT_SUCCESS:
          void dispatch('snackbar', {
            notificationType: SnackbarNotificationType.success,
            message: __('Comment rejected'),
            timeout: 3000,
          })
          break
        case ParamCommentModerationActionResult.LOGIN_REQUIRED:
          void dispatch('snackbar', {
            notificationType: SnackbarNotificationType.error,
            message: __('Please log in to your account'),
            timeout: 3000,
          })
          break
        case ParamCommentModerationActionResult.MODERATE_FAIL:
          void dispatch('snackbar', {
            notificationType: SnackbarNotificationType.error,
            message: __('Unable to moderate comment'),
            timeout: 3000,
          })
          break
      }
      clearSearchParam('moderate_comment_id', false)
      clearSearchParam('moderate_comment_action', false)
    },
    handlePostModerationParams({ dispatch }, paramPostModerationActionResult: ParamPostModerationActionResult) {
      // Show result of post reject/approve from query param
      switch (paramPostModerationActionResult) {
        case ParamPostModerationActionResult.APPROVE_SUCCESS:
          void dispatch('snackbar', {
            notificationType: SnackbarNotificationType.success,
            message: __('Post approved'),
            timeout: 3000,
          })
          break
        case ParamPostModerationActionResult.REJECT_SUCCESS:
          void dispatch('snackbar', {
            notificationType: SnackbarNotificationType.success,
            message: __('Post rejected'),
            timeout: 3000,
          })
          break
        case ParamPostModerationActionResult.LOGIN_REQUIRED:
          void dispatch('snackbar', {
            notificationType: SnackbarNotificationType.error,
            message: __('Please log in to your account'),
            timeout: 3000,
          })
          break
        case ParamPostModerationActionResult.MODERATE_FAIL:
          void dispatch('snackbar', {
            notificationType: SnackbarNotificationType.error,
            message: __('Unable to moderate post'),
            timeout: 3000,
          })
          break
      }
      clearSearchParam('moderate_post_id', false)
      clearSearchParam('moderate_post_action', false)
    },
    /* ------------
     * PANEL
     * ------------ */
    hideContributorsList({ commit }) {
      commit('HIDE_CONTRIBUTORS_LIST')
    },
    hideExtensionRequired({ commit }) {
      commit('HIDE_EXTENSION_REQUIRED')
    },
    hideInfo({ commit }) {
      commit('HIDE_INFO')
    },
    hideWallQuotaExceeded({ commit }) {
      commit('HIDE_WALL_QUOTA_EXCEEDED')
    },
    hideCommentsPanel({ commit }) {
      commit('HIDE_COMMENTS_PANEL')
    },
    hideToolMenu({ commit }) {
      commit('HIDE_TOOL_MENU')
    },
    hidePermitNotification({ commit }) {
      commit('HIDE_PERMIT_NOTIFICATION')
    },
    showReloadNotification({ commit }) {
      commit('SHOW_RELOAD_NOTIFICATION')
    },
    hideReloadNotification({ commit }) {
      commit('HIDE_RELOAD_NOTIFICATION')
    },
    showContributorsList({ commit }) {
      commit('SHOW_CONTRIBUTORS_LIST')
    },
    showExtensionRequired({ commit }) {
      commit('SHOW_EXTENSION_REQUIRED')
    },
    showOverFileSizeModal({ commit, dispatch, getters }, { file }) {
      dispatch('post/rejectPostsWithFileSizeExceeded', { file })
      commit('UPDATE_FILE_THAT_EXCEEDED_SIZE', file)
      if (getters.isLibraryWall) {
        const MAX_UPLOAD_BYTES = 1200000000 // 1.2GB
        if (!getters.amILibraryOwner) {
          dispatch('triggerOverFileSizeAlertDialog')
        } else {
          if (file.size > MAX_UPLOAD_BYTES) {
            dispatch('triggerOverFileSizeAlertDialog')
          } else {
            void dispatch('libraryPlans/fetchPlansAndShowUpgradeModal', {
              upgradeStep: LibraryUpgradeStep.ChooseTierFileSizeQuota,
              libraryId: getters.wallLibraryId,
              librarySlug: getters.librarySlug,
              makersCount: getters.libraryMakersCount,
              wallsUsed: getters.libraryWallsUsed,
              isDeprecatedFreeTier: getters.isLibraryDeprecatedFreeTier,
            })
          }
        }
      } else {
        const MAX_UPLOAD_BYTES = 600000000 // 600MB
        if (!getters.amIOwner) {
          dispatch('triggerOverFileSizeAlertDialog')
        } else {
          if (file.size > MAX_UPLOAD_BYTES) {
            dispatch('triggerOverFileSizeAlertDialog')
          } else {
            dispatch('plans/showPlanUpgradeModalCheckingScheduledChanges', {
              step: UpgradeStep.ChooseTierFileSizeQuota,
              membershipTier: getters.user.membership_tier,
              paymentSchedule: getters.user.period,
              upgradeSource: UpgradeSource.FileSizeLimit,
            })
          }
        }
      }
    },
    triggerOverFileSizeAlertDialog({ getters, dispatch }) {
      void dispatch('alertDialog/openAlertDialog', {
        iconSrc: anxiousFace,
        iconAlt: __('Anxious face'),
        title: __('File too large!'),
        body: __('This Padlet has a file size limit of %{num}.', {
          num: numberToHumanSize(getters.advertizedUploadLimit, { precision: 2, strip_insignificant_zeros: true }),
        }),
        xShadow: true,
      })
    },
    showInfo({ commit }) {
      commit('SHOW_INFO')
    },
    showWallQuotaExceeded({ commit }) {
      commit('SHOW_WALL_QUOTA_EXCEEDED')
    },
    showCommentsPanel({ commit }, { postId }) {
      commit('SHOW_COMMENTS_PANEL', postId)
    },
    showDeleteDialog({ dispatch, getters }) {
      dispatch('confirmationDialog/openConfirmationDialog', {
        ...DELETE_WALL_CONFIRMATION_PARAMS,
        afterConfirmActions: [{ type: 'deleteWall' }],
        xShadow: true,
      })
    },
    showClearAllDialog({ dispatch, getters }) {
      dispatch('confirmationDialog/openConfirmationDialog', {
        isCodeProtected: true,
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        title: __('Delete all posts?'),
        body: __('This action cannot be undone.'),
        confirmButtonText: __('Delete'),
        cancelButtonText: __('Nevermind'),
        afterConfirmActions: [{ type: 'post/clearAllPosts' }],
        xShadow: true,
        shouldFadeIn: false,
      })
    },
    checkAndSubscribeToPushNotifications({ dispatch }) {
      getNotificationPermission().then((permission) => {
        if (permission === 'granted') {
          dispatch('registerForPushNotifications')
        } else if (permission === 'default') {
          // Need user to trigger the request
          const message = __('Turn on notifications for this browser?')
          const actionText = __('OK')
          const actionName = 'registerForPushNotifications'
          dispatch(
            'snackbars/setSnackbar',
            { message, actionText, actionName, timeout: 6000, showCloseButton: true },
            { root: true },
          )
        }
        // Respect the user's decision if denied
        // else if (permission === 'denied') {
        //   // Displays unblocking instructions
        //   dispatch('registerForPushNotifications')
        // }
      })
    },
    registerForPushNotifications({ commit, state }) {
      registerForPushNotifications(state.user).catch((e) => {
        console.warn(`No push`, e)
        commit('SHOW_PERMIT_NOTIFICATION')
      })
    },
    showConvertLayoutPanel({ commit }) {
      commit('SHOW_CONVERT_LAYOUT_PANEL')
    },
    hideConvertLayoutPanel({ commit }) {
      commit('HIDE_CONVERT_LAYOUT_PANEL')
    },
    async requestGoogleDriveAuthorizationFromSurface({ commit, getters }, { userId, tenantId }) {
      const result = await useGoogleDrive().requestGoogleDriveAuthorization({ userId, tenantId })
      trackEvent('GoogleDrive', 'Connection completed on surface')
      // Set Google Drive Email in frontend for use in UI
      useGoogleDrivePickerStore().setGoogleDriveEmail(result.email)
      useGoogleDrivePickerStore().setGoogleDriveAccessToken(result.token)
    },
    async convertWallLayout({ commit, getters }, { newLayout }) {
      try {
        await PadletApi.Wall.convertLayout({
          id: getters.wallId,
          newLayout,
        })
        // This is a special case when we convert a grid (shelf) to a grid (wall).
        // When we do that, the format doesn't change and it won't trigger the `loadNewFormat`
        // watcher in `surface_container.vue`. The page will be stuck trying to convert
        // the format. We need to manually reload the page.
        // You may wonder: why do we need to reload if the format is the same?
        // The answer is: `wall.wish_arrangement` is not real-time right now so we don't
        // know if the server has updated `wall.wish_arrangement.group_by`. We can do
        // a `wall.touch` to publish an `edit_wall` event but it comes with the same
        // `uid` so it won't trigger `handleMessage` in `app/javascript/vuexstore/modules/realtime.ts`
        // So the easiest way is to just reload. Our users are used to it anyway.
        if (getters.format === 'grid' && newLayout === 'grid') {
          reload()
        } else {
          commit('UPDATE_WALL', { viz: newLayout })
        }
      } catch {
        const errorMessage = __('Failed to convert layout. Please try again.')
        commit('CONVERT_LAYOUT_FAILED', errorMessage)
      }
    },
    showRemakePanel({ commit }) {
      commit('SHOW_REMAKE_PANEL')
    },
    hideRemakePanel({ commit }) {
      commit('HIDE_REMAKE_PANEL')
    },
    toggleToolMenu({ commit, state }) {
      if (state.xToolMenu) {
        commit('HIDE_TOOL_MENU')
      } else {
        commit('SHOW_TOOL_MENU')
      }
    },
    showReportPanel({ commit }, { postId }: { postId?: Id } = {}) {
      commit('SHOW_REPORT_PANEL', postId)
    },
    hideReportPanel({ commit }) {
      commit('HIDE_REPORT_PANEL')
    },
    showDevelopersPanel({ commit }) {
      commit('SHOW_DEVELOPERS_PANEL')
    },
    hideDevelopersPanel({ commit }) {
      commit('HIDE_DEVELOPERS_PANEL')
    },
    showActionBarMoreMenu({ commit }) {
      commit('SHOW_ACTION_BAR_MORE_MENU')
    },
    hideActionBarMoreMenu({ commit }) {
      commit('HIDE_ACTION_BAR_MORE_MENU')
    },

    /* ------------
     * POST
     * ------------ */
    deletePostRemote({ dispatch, state }, post) {
      if (state.commentsPanelPostId == post.id) {
        dispatch('hideCommentsPanel')
      }
      // postAction/deletePostRemote must come before post/deletePostRemote
      // the post action panels(e.g.post action menu, copy / transfer panels) hold their own cid value and will try to access getPostByCid(cid)
      // if we delete the post first, it will cause some undefined object errors
      dispatch('postAction/deletePostRemote', post)
      dispatch('post/deletePostRemote', post)
    },
    removePost({ dispatch }, { id, cid }) {
      dispatch('post/removePost', { id, cid })
    },
    addPost({ dispatch }, payload) {
      const postAttributes = payload.post
      dispatch('post/addPost', postAttributes)
    },
    /* ------------
     * ATTACHMENT
     * ------------ */
    resizeEditor({ commit }, { height }) {
      commit('RESIZE_EDITOR', height)
    },

    /* ------------
     * HEADER
     * ------------ */
    resizeHeader({ commit }, { height }) {
      commit('RESIZE_HEADER', height)
    },

    /* ------------
     * WALL
     * ------------ */
    updateWall({ commit }, changes) {
      commit('UPDATE_WALL', changes)
    },
    async setCoverPost({ commit, dispatch, state }, { postId }) {
      const wall = await saveWall({ ...state.wall, cover_wish_id: postId })
      commit('UPDATE_WALL', wall)
      dispatch('snackbar', {
        notificationType: SnackbarNotificationType.success,
        message: __('Cover image updated'),
      })
    },
    async unsetCoverPost({ commit, dispatch, state }) {
      const wall = await saveWall({ ...state.wall, cover_wish_id: null })
      commit('UPDATE_WALL', wall)
      dispatch('snackbar', {
        notificationType: SnackbarNotificationType.success,
        message: __('Cover image updated'),
      })
    },
    async archiveWall({ getters }) {
      await PadletApi.Wall.archive(getters.wallId)
      safeStorage.setItem('activePadletFilter', 'archived')
      navigateTo('/')
    },
    async deleteWall({ getters }) {
      await deleteWall(getters.wallId)
      navigateTo('/')
    },
    async trashWall({ getters }) {
      await DashboardWallApi.trash(getters.wallId)
      setCookie('surface_wall_trash', 'true')
      if (getters.libraryDashboardUrl !== undefined) {
        const libraryUrl = transformUrl(getters.libraryDashboardUrl, {
          searchParams: {
            mobile_page: MobilePage.Collection,
            filter: 'all',
          },
        })
        navigateTo(libraryUrl)
        return
      }
      const userDashboardUrl = transformUrl(buildUrlFromPath('/dashboard'), {
        searchParams: {
          mobile_page: MobilePage.Collection,
          filter: 'all',
        },
      })
      navigateTo(userDashboardUrl)
    },
    async markWallAsTemplate({ dispatch, commit, getters }) {
      commit('MARK_WALL_AS_TEMPLATE')
      try {
        await PadletApi.Wall.markAsTemplate(getters.wallId, true)
      } catch (error) {
        void dispatch('genericFetchError', { error })
        commit('UNMARK_WALL_AS_TEMPLATE')
      }
    },
    async unmarkWallAsTemplate({ dispatch, commit, getters }) {
      commit('UNMARK_WALL_AS_TEMPLATE')
      try {
        await PadletApi.Wall.markAsTemplate(getters.wallId, false)
      } catch (error) {
        void dispatch('genericFetchError', { error })
        commit('MARK_WALL_AS_TEMPLATE')
      }
    },

    /*****************
     * BOOKMARKS
     *****************/

    async bookmarkWallAndNotify({ dispatch, getters }) {
      const wallId = getters.wallId

      try {
        await dispatch('wallCollections/addBookmark', { wallId })
        await dispatch('snackbar', {
          notificationType: SnackbarNotificationType.success,
          message: __('Bookmark added'),
          actionText: __('Edit'),
          actionTextActions: [{ type: 'openEditBookmarksDialog' }],
        })
      } catch (error) {
        void dispatch('genericFetchError', { error })
      }
    },
    async addToFavorites({ dispatch, getters }) {
      const wallId = getters.wallId
      try {
        await dispatch('wallCollections/addBookmark', { wallId })
      } catch (error) {
        void dispatch('genericFetchError', { error })
      }
    },
    async removeFromFavorites({ dispatch, getters }) {
      const wallId = getters.wallId

      try {
        await dispatch('wallCollections/removeBookmark', { wallId })
      } catch (error) {
        void dispatch('genericFetchError', { error })
      }
    },
    openEditBookmarksDialog({ commit }, anchor = null) {
      commit('SHOW_EDIT_BOOKMARKS_DIALOG', anchor)
    },
    closeEditBookmarksDialog({ commit }) {
      commit('HIDE_EDIT_BOOKMARKS_DIALOG')
    },
    promptNameForNewFolder({ dispatch }) {
      void dispatch('inputDialog/openInputDialog', {
        title: __('New folder'),
        inputPlaceholder: __('Folder name'),
        inputMaxLength: MAX_FOLDER_NAME_LENGTH,
        inputAriaLabel: __('Enter the folder name'),
        validationActions: [{ type: 'validateFolderName' }],
        submitActions: [{ type: 'createFolder' }],
      })
    },
    async createFolder({ dispatch }, { inputValue }) {
      const { success } = await dispatch('wallCollections/createFolderForActiveUser', {
        name: inputValue,
      })
      if (!(success as boolean)) return
      await dispatch('snackbar', {
        notificationType: SnackbarNotificationType.success,
        message: __('Created new folder'),
      })
    },
    validateFolderName({ dispatch }, { inputValue, disallowedNames }) {
      const disable = !isValidFolderName(inputValue) || (disallowedNames || []).includes(inputValue)
      dispatch('inputDialog/setSubmitButtonDisable', { disable })
    },

    // SCROLL
    announceWishListMounted({ commit }) {
      commit('ANNOUNCE_WISH_LIST_MOUNTED')
    },
    /* ------------
     * POST CONNECTION
     * ------------ */
    removeConnectionsForPost({ dispatch }, { postId }) {
      dispatch('postConnection/removeConnectionsForPost', { postId })
    },

    fallbackToRestApi({ dispatch, commit }) {
      commit('SET_SHOULD_USE_REST_API', true)
      void dispatch('refresh', { isAutoRefresh: false })
    },

    // REFRESH
    refresh({ commit, dispatch, state, getters }, { isAutoRefresh }) {
      if (!state.isOnline) return
      if (!getters.isWallSyncNeeded) return

      // `refresh` will apply all the pending realtime updates.
      // This is not immune to race conditions, but since it is harmless to omit this is the first place,
      // this is sufficient to reduce user annoyance due to outdated notifications.
      dispatch('realtime/clearDispatchQueue')

      const mapWasFittingMarkerBounds = getters['map/mapDoesFitMarkersBounds']
      dispatch('map/disableMapFittingMarkersBounds')

      // We are not updating the vuexstore on auto refresh, the network response will update service worker cache
      // and if the cache is update, it will send a notification to update the store

      const updateOrderAndMapIfNecessary = () => {
        if (mapWasFittingMarkerBounds) {
          dispatch('map/enableMapFittingMarkersBounds')
        }
      }

      const shouldResetState = !isAutoRefresh

      if (state.shouldFallbackWsDataFetchToRest || !isAppUsing('realtimeFetching')) {
        if (isAppUsing('accumulatedReactions')) {
          void useReactionsStore().fetchAccumulatedReactions()
        } else {
          void dispatch('reaction/fetchReactions', { shouldResetState })
        }

        void dispatch('comment/fetchComments', { shouldResetState })
        dispatch('post/fetchPosts', { shouldResetState }).then(updateOrderAndMapIfNecessary)

        if (isAppUsing('layoutFormatSettingV2') || getters.canUseSections) {
          dispatch('section/fetchSections')
        }
      }

      if (isAppUsing('layoutFormatSettingV2') || getters.canHaveConnections) dispatch('postConnection/fetchConnections')

      dispatch('contributingStatus/ping')

      // reactions, comments, wall sections, ... only do 1 request
      // but wishes have pagination and may make multiple requests
      // => for first page load, wait for it to be done before marking things as done
      if (getters.isInitialPaginatedDataLoadDone) {
        commit('REFRESH_DONE')
      } else {
        const unwatch = this.watch(
          (state, getters) => getters.isInitialPaginatedDataLoadDone,
          (newValue, oldValue) => {
            if (newValue) {
              commit('REFRESH_DONE')
              unwatch()
            }
          },
        )
      }
    },

    // User setting
    async fetchUserLibrarySettings({ commit }) {
      try {
        const response = await PadletApi.User.fetchUserLibrarySettings()
        const userLibrarySettings = (response?.data as JsonAPIResource<UserLibrarySettingsApiResponse>).attributes
        commit('SET_USER_LIBRARY_SETTINGS', userLibrarySettings)
      } catch (e) {
        captureFetchException(e, { source: 'SurfaceFetchUserLibrarySettings' })
      }
    },
    // LIBRARIES
    async fetchCreatableLibraries({ commit }, { userId }) {
      const response = await PadletApi.Library.fetchCreatableLibraries({ userId })
      const librariesData = response.data as Array<JsonAPIResource<Library>>
      commit('SET_CREATABLE_LIBRARIES', { libraries: librariesData.map((library) => library.attributes) })
    },
    async fetchViewableLibraries({ commit }, { userId }) {
      const response = await PadletApi.Library.fetchViewableLibraries({ userId })
      const librariesData = response.data as Array<JsonAPIResource<Library>>
      commit('SET_VIEWABLE_LIBRARIES', { libraries: librariesData.map((library) => library.attributes) })
    },

    // SNACKBAR
    snackbar({ dispatch }, snackbarPayload) {
      dispatch('globalSnackbar/setSnackbar', snackbarPayload)
    },
    genericFetchError({ dispatch }, { error }) {
      captureNonNetworkFetchError(error)
      void dispatch('globalSnackbar/genericFetchError')
    },

    loginAndRedirectTo(_, link) {
      navigateTo(loginWithReferrerUrl(link))
    },
    showPromptToLoginDialog({ dispatch, state }, payload: Partial<ConfirmationDialogPayload>): void {
      const defaultPromptToLoginDialogPayload = {
        body: __('Please log in to your Padlet account or sign up for one.'),
        confirmButtonText: __('Log in'),
        cancelButtonText: __('Nevermind'),
        afterConfirmActions: [{ type: 'loginAndRedirectTo', payload: state.wall.links.permanent }],
      }
      void dispatch('confirmationDialog/openConfirmationDialog', {
        ...defaultPromptToLoginDialogPayload,
        ...payload,
      })
    },

    // FOLLOWS
    updateWallFollows(
      { commit, getters },
      { wallFollowId, followsWall }: { wallFollowId: Id; followsWall: boolean },
    ): void {
      commit('UPDATE_WALL_FOLLOWS', {
        current_user_follows_wall: followsWall,
        wall_follow_id: wallFollowId,
        wall_follows_count: followsWall ? getters.wallFollowsCount + 1 : getters.wallFollowsCount - 1,
      })
    },
    remoteUpdateWallFollows({ commit }, { wall_follow_id, follows }: { wall_follow_id: Id; follows: boolean }): void {
      commit('UPDATE_WALL_FOLLOWS', {
        current_user_follows_wall: follows,
        wall_follow_id,
      })
    },
    remoteUpdateWallFollowsCount({ commit, getters }, { follows }: { follows: boolean }): void {
      commit('UPDATE_WALL_FOLLOWS', {
        wall_follows_count: follows ? getters.wallFollowsCount + 1 : getters.wallFollowsCount - 1,
      })
    },

    // PRIVACY OPTIONS
    async fetchWallPrivacyOptions({ commit, getters }): Promise<void> {
      try {
        const response = await PadletApi.WallPrivacyOptions.fetchWallPrivacyOptions(getters.wallId)
        const wallPrivacyOptions = response.data as JsonAPIResource<CanSetWallPrivacyOptions>
        const privacyOptionsPermissions = wallPrivacyOptions.attributes.privacyOptions
        commit('SET_PRIVACY_OPTION_PERMISSIONS', privacyOptionsPermissions)
      } catch (e) {
        captureFetchException(e, { source: 'SurfaceFetchWallPrivacyOptions' })
      }
    },
  },
}

// Define a local type for now
interface BrowserWindow {
  ww?: any
}
const thisWindow = window as unknown as BrowserWindow

/**
 * The old dispatch function signature is different from the vuex store dispatch function signature
 * We write the dispatch function in ww.xstore so that any method signature will work.
 * ```js
 * ww.xstore.dispatch({type: 'START_EDITING_POST', payload: {postCid: id}})
 * ww.xstore.dispatch('START_EDITING_POST', {postCid: id})
 * ww.xstore.dispatch(function (dispatch) => {dispatch({type: 'START_EDITING_POST', payload: {postCid: id}})}
 * ```
 */

export function setupNativeBridgeXstore(store): void {
  const oldDispatch = (obj) => store.dispatch(obj.type, obj)

  if (!thisWindow.ww) {
    thisWindow.ww = {}
  }
  thisWindow.ww.xstore = {
    store,
    state: {},
    getters: {},
    dispatch: (obj, payload) => {
      if (typeof obj === 'string') {
        store.dispatch(obj, payload)
      } else if (obj.type) {
        oldDispatch(obj)
      } else if (typeof obj === 'function') {
        obj(oldDispatch)
      }
    },
  }

  store.subscribe(() => {
    thisWindow.ww.xstore.state = store.state
    thisWindow.ww.xstore.getters = store.getters
  })
}
